import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Actions, ofType } from '@ngrx/effects';
import { IEstensaButtonStatus, SeFormulaParser } from '@se/se.ui.advanced';
import { ButtonsTypes } from 'estensa-module';
import { BehaviorSubject, firstValueFrom, Subject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import {
    EIntegrationType,
    EWhere,
    FullIntegrationStoreData,
    IDisabledConfig,
    IExternalIntegration,
    IOnCallActionData,
    IRefreshTokenData,
    isRefreshTokenData,
    PairingTokenBundle,
} from './tp-integrations.interface';
import { seToolbarButtonPressed } from '../../se-toolbar/se-buttons-toolbar/redux/se-buttons-toolbar.actions';
import { modelServiceJsonToIntegrationStoreData } from './routines';
import { IDialogData, StackManagerComponent } from './stack-manager/stack-manager.component';
import { ToolbarAction, ToolbarButtonData } from '../../se-toolbar/se-buttons-toolbar/types';
import { Json } from '@se/cp.common';
import { Logger, ServerRootService } from '@se/se.ui.html5';
import moment from 'moment';
import { TpIntegrationsCacheService } from './tp-integrations-cache.service';
import { SsoLoginService } from '../../sso/sso-login.service';

interface TpIntegrationsData {
    id: string;
    data: FullIntegrationStoreData;
}

@Injectable({
    providedIn: 'root',
})
export class TpIntegrationsService {
    static readonly REFRESH_TOKEN_TIMEOUT_DELTA = moment.duration(15, 'seconds');

    // Mappa che associa dati aggiuntivi per ogni azione in esecuzione.
    private readonly runningActions: Record<string, unknown> = {};

    subscriptions: Subscription[] = [];
    /*decidere se cablarne la location nell'appsettings.json
     * usare comunque un http.get per evitare che venga compilato col bundle
     * @UPDATE il json verra diviso in due parti, una che contiene solo le configurazioni non rischiose
     * type=onCall che rimarra in locale
     * il resto verra messo su db */
    readonly BASE_CONFIG_PATH = 'assets/third-parties-configurations.json';
    config: IExternalIntegration[] = [];
    actionConfig: IExternalIntegration[] = [];
    // forse deve essere inserito un semaforo per inibire la chiamata a due code nello stesso intervallo di tempo. - Finché non è finita una coda non si può elaborare un altra coda
    askData$: Subject<string> = new Subject();
    data$: BehaviorSubject<TpIntegrationsData | null> = new BehaviorSubject<TpIntegrationsData | null>(null); // id della coda
    stackManagerDialog: MatDialogRef<StackManagerComponent> | null = null;

    /*osservabile per avvisare quando il servizio e` operativo a gestire le action di tipo BUTTON*/
    isReady$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    /*osservabile per avvisare quando il servizio e` operativo a gestire le action ON_CALL
     * viene fatta un distinzione tra i tipi di action perche in futuro i button verranno gestiti con json su db
     * mentre le on-call non hanno issue di sicurezza tali per cui possono essere gestite con json nella cartella assets*/
    isActionReady$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    version: string = '1.4';

    constructor(
        private actions: Actions,
        private cache: TpIntegrationsCacheService,
        private http: HttpClient,
        private dialog: MatDialog,
        private logger: Logger,
        private serverRootService: ServerRootService,
        private ssoLoginService: SsoLoginService,
    ) {
        this.subscriptions.push(
            this.actions
                .pipe(
                    ofType(seToolbarButtonPressed),
                    filter((action) => {
                        return action.button.indexOf(`${ToolbarAction.CALL_THIRD_PARTIES}`) !== -1;
                    }),
                )
                .subscribe((action) => {
                    const { payload } = action;
                    //payload.id e` l'id della callstack da chiamare
                    if (!payload?.id) return;
                    // emettere dammi i dati.
                    this.askData$.next(payload.id);
                    // ascolta i dati in data$
                }),
        );
        this.subscriptions.push(
            this.data$.pipe(
                filter((data) => Boolean(data)),
            ).subscribe(({ id, data }) => this.onData(id, data)),
        );
    }

    onCallAction(actionData: IOnCallActionData) {
        const { action, data } = actionData;

        const externalIntegrationData = this.actionConfig.find((c) => c.id === data.actionId);
        if (!externalIntegrationData) {
            return;
        }

        this.preParseCalls(externalIntegrationData);

        switch (action) {
            case 'REFRESH_TOKEN':
                if (!isRefreshTokenData(data)) {
                    throw new Error(`error data for refresh token is not in valid format`);
                }
                if (data.isReset) {
                    this.onResetRefreshTokenAction(data);
                } else {
                    this.onRefreshTokenAction(data, Boolean(externalIntegrationData.test));
                }
                break;
            default:
                throw new Error(`error no service available for action ${action}`);
        }
    }

    static createDataFromJsonModel = (modelData: Json) => {
        return modelServiceJsonToIntegrationStoreData(modelData);
    };

    async init(path = this.BASE_CONFIG_PATH) {
        return this.loadConfigurationData(path);
    }

    updateButtonStatus = (where: EWhere, data: any): Partial<Record<ToolbarAction, IEstensaButtonStatus>> => {
        const buttonsConfigs = this.getButtonsConfig(where, data);
        return Object.fromEntries(this.getButtonsKey(where).map((key, i) => [key, buttonsConfigs[i]]));
    };

    compareEstensaButtons(
        buttonsBefore: Partial<Record<ToolbarAction, IEstensaButtonStatus>>,
        buttonsAfter: Partial<Record<ToolbarAction, IEstensaButtonStatus>>,
    ): boolean {
        const buttonsBeforeList = Object.values(buttonsBefore);
        const buttonsAfterList = Object.values(buttonsAfter);
        if (buttonsBeforeList.length !== buttonsAfterList.length) return false;
        return buttonsAfterList.reduce((acc, button, i) => {
            return acc && button.disabled === buttonsBeforeList[i].disabled;
        }, true);
    }

    checkConfigExists(where: EWhere): boolean {
        return this.config.filter((cnf) => cnf.where === where).length > 0;
    }

    mergeButtonsData(buttons: ToolbarButtonData[], where: EWhere): ToolbarButtonData[] {
        const mergedButtons: ToolbarButtonData[] = [];
        const buttonsList = this.makeButtonsData(where);
        buttonsList.sort((a, b) => a[0] - b[0]);
        const integrationsButtonsRecord = Object.fromEntries(buttonsList);
        const L = buttons.length;
        let pos = 0;
        let totalPos = 0;
        while (pos < L) {
            if (integrationsButtonsRecord[totalPos]) {
                mergedButtons.push(integrationsButtonsRecord[totalPos]);
            } else {
                mergedButtons.push(buttons[pos]);
                pos++;
            }
            totalPos++;
        }

        const outOfRangeButtons = buttonsList.filter((button) => button[0] >= L);
        mergedButtons.push(...outOfRangeButtons.map((button) => button[1]));
        return mergedButtons;
    }

    parseDisabled(dataStore: any, disabledData: IDisabledConfig): boolean {
        const { formula, placeholders } = disabledData;
        const formulaParser = new SeFormulaParser();
        placeholders.forEach((p: string, i: number) => formulaParser.setVariable(`B_${i}`, dataStore[p]));
        const formulaResult = formulaParser.parse(formula);
        if (formulaResult.error) {
            this.logger.error(`Error calculating parseDisabled formula: ${formulaResult.error}`);

            return true; //in caso di errore si forza la disabilitazione
        }
        return Boolean(formulaResult.result);
    }

    private onData(id: string, data: FullIntegrationStoreData) {
        console.log(
            `%cThird parties integration service version: %c${this.version}`,
            'color:#2a9d8f; padding:2px;',
            'background-color:#264653; color:white; font-size:110%; border-radius:3px; padding:2px;',
        );

        //inizia chiamata della pila di esecuzione.
        const externalIntegrationData = this.config.find((c) => c.id === id);
        if (!externalIntegrationData) return; //@TODO error missing configuration
        this.preParseCalls(externalIntegrationData);

        const dialogData: IDialogData = {
            id,
            data,
            externalIntegrationData,
        };

        //@ts-ignore
        this.stackManagerDialog = this.dialog.open(StackManagerComponent, {
            disableClose: true,
            width: '100vw',
            height: '100vh',
            data: dialogData,
            panelClass: 'tp-dialog-container',
            position: {
                top: 0,
                left: 0,
            },
        });

        this.stackManagerDialog.afterClosed().subscribe((_) => {
            this.stackManagerDialog = null;
            this.data$.next(null);
        });
    }

    private preParseCalls(integrationData: IExternalIntegration) {
        if (integrationData.test && integrationData.calls?.length) {
            integrationData.calls.forEach((call) => {
                call.test = true;
            });
        }
    }

    /**************************** TOKEN SECTION ****************************/

    private getServerRoot(test = false): string {
        return test ? 'http://localhost:43210' : this.serverRootService.getBaseRoot();
    }

    private onRefreshTokenAction(data: IRefreshTokenData, test = false) {
        const { actionId, refreshUri, cacheKey, cacheInitialValue } = data;
        if (cacheInitialValue) {
            this.cache.set(cacheKey, cacheInitialValue);
        }
        this.runningActions[data.actionId] = this.refreshToken(actionId, cacheKey, refreshUri, test);
    }

    private refreshToken(actionId: string, cacheKey: string, refreshUri: string, test = false) {
        const token = this.cache.get(cacheKey) as PairingTokenBundle;
        const uri = `${this.getServerRoot(test)}${refreshUri}`;
        const refreshTimeout = moment
            .duration(token.expires_in || 5, 'seconds')
            .subtract(TpIntegrationsService.REFRESH_TOKEN_TIMEOUT_DELTA)
            .asMilliseconds();

        return window.setTimeout(async () => {
            this.logger.debug(`Refreshing SSO token: ${token} with ${uri}`);
            try {
                const res = await firstValueFrom(this.http.post<PairingTokenBundle>(uri, token));
                if (this.runningActions[actionId] === null) {
                    // È possibile che l'azione sia stata cancellata.
                    return;
                }
                const { access_token, expires_in, refresh_token, id_token } = res;

                this.logger.debug(`SSO Token refreshed`);

                this.cache.set(cacheKey, { access_token, expires_in, refresh_token, id_token });
                this.ssoLoginService.setOpenIdToken(id_token);
                this.runningActions[actionId] = this.refreshToken(actionId, cacheKey, refreshUri);
            } catch (err) {
                this.logger.error(`Error while refreshing SSO token: ${err.message}`);

                this.runningActions[actionId] = null;
                this.cache.set(cacheKey, null);
                this.ssoLoginService.removeOpenIdToken();
                throw err;
            }
        }, refreshTimeout);
    }

    private onResetRefreshTokenAction = (data: IRefreshTokenData) => {
        const actionData = this.runningActions[data.actionId];
        if (typeof actionData === 'number') {
            clearTimeout(actionData);
        }
        this.runningActions[data.actionId] = null;
    };

    /********************* END TOKEN SECTION ****************/

    private async loadConfigurationData(path = this.BASE_CONFIG_PATH) {
        try {
            const allConfig = await firstValueFrom(this.http.get<IExternalIntegration[]>(path));
            /*!== ON_CALL per retrocompatibilita, altrimenti i vecchi json che non hanno type=BUTTON rimuovono i buttons*/
            this.config = allConfig.filter(
                (cnf) => cnf.type !== EIntegrationType.ON_CALL /*=== EIntegrationType.BUTTON*/,
            );
            this.actionConfig = allConfig.filter((cnf) => cnf.type === EIntegrationType.ON_CALL);
            this.isReady$.next(true);
            this.isActionReady$.next(true);
            return true;
        } catch (err) {
            this.logger.error(`Error loading configuration data: ${err.message}`);
        }
    }

    private getButtonsKey(where: EWhere): ToolbarAction[] {
        return this.config
            .filter((cnf) => cnf.where === where)
            .map((cnf) => {
                const { id } = cnf;
                return `${ToolbarAction.CALL_THIRD_PARTIES}_${id}` as ToolbarAction;
            });
    }

    private getButtonsConfig(where: EWhere, data: any): IEstensaButtonStatus[] {
        let forceDisabled: boolean = false;
        if (!data || (Array.isArray(data) && data.length !== 1)) forceDisabled = true;
        let dataStore = Array.isArray(data) ? data[0] : data;
        dataStore = modelServiceJsonToIntegrationStoreData(dataStore);
        return this.config
            .filter((cnf) => cnf.where === where)
            .map((cnf) => {
                const { disabled, alwaysEnabled } = cnf;
                const config = { visible: true, disabled: false };
                if (forceDisabled) config.disabled = true;
                else if (disabled) config.disabled = this.parseDisabled(dataStore, disabled);
                if (alwaysEnabled) config.disabled = false;
                return config;
            });
    }

    private makeButtonsData(where: EWhere): [number, ToolbarButtonData][] {
        return this.config
            .filter((cnf) => cnf.where === where)
            .map((cnf) => {
                const { label, description, id } = cnf;
                let position = cnf.position ?? -1;
                position = position >= 0 ? position : Infinity;
                return [
                    position,
                    {
                        label,
                        permission: ButtonsTypes.externalLinks,
                        isToggle: false,
                        hasChildren: false,
                        status: { visible: true, disabled: false, checked: false },
                        payload: { id, description },
                        actionType: `${ToolbarAction.CALL_THIRD_PARTIES}_${id}` as ToolbarAction,
                    },
                ];
            });
    }
}
